import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import usePageTitle from '../../../../hooks/usePageTitle';
import {useBreadcrumbs} from '../../../../contexts/BreadcrumbContext';
import PaymentsActionsPanel from '../../../../components/PaymentPageActions';
import {fetchEmailLog} from '../../../../slices/paymentGroupsSlice';
import {getLocalTimezoneLabel} from '../../../../utils/datetime';

export default function EmailHistory() {
  usePageTitle('Email history');
  useBreadcrumbs(
    [{label: 'Forecasters', href: '/users/payments'}, {label: 'Email history'}],
    'Audit log of payment emails: what went out, when, to whom');

  const dispatch = useDispatch();
  const {data: rows, status} = useSelector((state) => state.paymentGroups.emailLog);

  const currentDate = new Date();
  const prevMonthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
  const defaultYear = String(prevMonthDate.getFullYear());
  const defaultMonth = String(prevMonthDate.getMonth() + 1).padStart(2, '0');

  const [year, setYear] = useState(defaultYear);
  const [month, setMonth] = useState(defaultMonth);
  const [appliedYear, setAppliedYear] = useState(year);
  const [appliedMonth, setAppliedMonth] = useState(month);

  const loadLog = (y, m) => {
    dispatch(fetchEmailLog({year: y, month: m}));
    setAppliedYear(y);
    setAppliedMonth(m);
  };

  // Mount-only fetch: year/month are uncontrolled selects until "Apply" is
  // pressed, so this must not re-run on every keystroke/selection change.
  // The empty dependency array is intentional; loadLog reads the state
  // that is current when this effect body runs, on mount only.
  useEffect(() => {
    loadLog(year, month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => loadLog(year, month);

  return (
    <div className="space-y-6">
      <PaymentsActionsPanel
        year={year}
        month={month}
        appliedYear={appliedYear}
        appliedMonth={appliedMonth}
        onYearChange={setYear}
        onMonthChange={setMonth}
        onApply={handleApply}
      />

      <div className="bg-surface dark:bg-gray-870 rounded-lg border border-gray-200/70
                      dark:border-gray-600/70 shadow-sm overflow-hidden">
        {status === 'failed' ? (
          <p className="p-6 text-sm text-red-600 dark:text-red-400">
            Could not load the email log. Reload the page to try again.
          </p>
        ) : status === 'loading' ? (
          <p className="p-6 text-sm text-gray-500 dark:text-gray-400">
            Loading email log...
          </p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
            No emails logged yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left uppercase tracking-wide text-gray-500
                               dark:text-gray-400 border-b border-gray-200/70
                               dark:border-gray-600/70">
                  <th className="px-4 py-2 font-semibold">When</th>
                  <th className="px-4 py-2 font-semibold">Scope</th>
                  <th className="px-4 py-2 font-semibold">Subject</th>
                  <th className="px-4 py-2 font-semibold">Recipient</th>
                  <th className="px-4 py-2 font-semibold">CC</th>
                  <th className="px-4 py-2 font-semibold">Triggered by</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((row, i) => {
                  const failed = row.status === 'failed';
                  return (
                    <tr key={i} className={failed ? 'bg-red-50 dark:bg-red-900/10' : ''}>
                      <td className="px-4 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">
                        {new Date(row.sent_at).toLocaleString()} {getLocalTimezoneLabel(row.sent_at)}
                      </td>
                      <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                        {row.scope_label}
                      </td>
                      <td className="px-4 py-2 max-w-xs truncate text-gray-700 dark:text-gray-300"
                          title={row.subject}>
                        {row.subject}
                      </td>
                      <td className="px-4 py-2 font-mono text-gray-900 dark:text-gray-100">
                        {row.recipient}
                        {row.forecaster_opted_out && (
                          <span className="ml-2 font-sans text-[11px] font-semibold
                                           text-amber-600 dark:text-amber-400"
                                title="Forecaster opted out of invoice emails:
                                       no copy was sent to them">
                            opted out, no copy sent
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 font-mono max-w-[16rem] break-words
                                     text-gray-500 dark:text-gray-400">
                        {(row.cc || []).join(', ')}
                      </td>
                      <td className="px-4 py-2 text-gray-500 dark:text-gray-400">
                        {row.triggered_by || '-'}
                      </td>
                      <td className="px-4 py-2">
                        <span className={failed
                          ? 'text-red-600 dark:text-red-400 font-semibold'
                          : 'text-green-600 dark:text-green-400 font-semibold'}>
                          {row.status}
                        </span>
                        {failed && row.error && (
                          <p className="text-red-500 dark:text-red-400 mt-0.5">
                            {row.error}
                          </p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
